'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { apiFetch } from '@/platform/lib/api';

const MIN_VALIDITY_POSITIVE_MASS = 500;
const MIN_COMMUNITY_VALIDITY_RATING = 4.0;

export interface CommunityValidity {
  communityValidityRating?: number;
  validityPositiveMass?: number;
}

export interface ClaimBadgesProps {
  legitimacyAvg?: number;
  legitimacyRatingCount?: number;
  communityValidity?: CommunityValidity;
  communityApproved?: boolean;
  hasVideo?: boolean;
  hasLocation?: boolean;
  hasPurchasePage?: boolean;
  purchasePageUrl?: string;
  vendorLinkStatus?: 'none' | 'pending' | 'approved' | 'rejected';
}

function hasCommunityVotes(communityValidity?: CommunityValidity) {
  return (communityValidity?.validityPositiveMass ?? 0) > 0;
}

function formatValidityRating(communityValidity?: CommunityValidity) {
  if (!hasCommunityVotes(communityValidity)) {
    return '—';
  }
  return communityValidity?.communityValidityRating?.toFixed(1) ?? '—';
}

function useViewerInfluenceRank() {
  const { user } = useAuth();
  const [rank, setRank] = useState<number | null>(null);

  useEffect(() => {
    if (!user) {
      setRank(null);
      return;
    }
    apiFetch<{ voterValidityPoints: number }>(`/users/${user.uid}/activity`)
      .then((activity) => setRank(activity.voterValidityPoints))
      .catch(() => setRank(null));
  }, [user]);

  return rank;
}

export function ClaimBadges({
  legitimacyAvg,
  legitimacyRatingCount,
  communityValidity,
  communityApproved,
  hasVideo,
  hasLocation,
  hasPurchasePage,
  purchasePageUrl,
  vendorLinkStatus,
}: ClaimBadgesProps) {
  const mass = communityValidity?.validityPositiveMass ?? 0;
  const ratingLabel = formatValidityRating(communityValidity);
  const viewerRank = useViewerInfluenceRank();
  const rankHint =
    viewerRank != null
      ? `Your influence rank: ${viewerRank} / 300.`
      : 'Sign in to see your influence rank (1–300).';

  return (
    <div className="claim-badges" aria-label="Claim badges">
      <span
        className="claim-badges__validity"
        title={`Community validity ${ratingLabel} / 5.0. Approval weight ${Math.round(mass)} / ${MIN_VALIDITY_POSITIVE_MASS}. ${rankHint}`}
      >
        Validity {ratingLabel} / 5.0
      </span>
      {communityApproved && (
        <span className="claim-badges__approved" title="Community-approved haul — eligible for find listings">
          ✓ Community approved
        </span>
      )}
      {!communityApproved && (
        <span
          className="claim-badges__pending"
          title={`Needs ${MIN_COMMUNITY_VALIDITY_RATING}+ rating and ${MIN_VALIDITY_POSITIVE_MASS} approval weight. ${rankHint}`}
        >
          Pending approval ({Math.round(mass)} / {MIN_VALIDITY_POSITIVE_MASS} weight)
        </span>
      )}
      {legitimacyRatingCount != null && legitimacyRatingCount > 0 && (
        <span className="claim-badges__stars" title={`${legitimacyAvg?.toFixed(1)} from ${legitimacyRatingCount} ratings`}>
          ★ {legitimacyAvg?.toFixed(1)} ({legitimacyRatingCount})
        </span>
      )}
      {hasVideo && <span className="claim-badges__video" title="Owner linked a video">🎬</span>}
      {hasLocation && <span className="claim-badges__location" title="Physical location pinned">📍</span>}
      {hasPurchasePage && (
        <span className="claim-badges__purchase" title={`Purchase page: ${purchasePageUrl ?? 'online listing'}`}>
          🔗
        </span>
      )}
      {vendorLinkStatus === 'approved' && (
        <span className="claim-badges__vendor" title="Linked vendor store">
          🏪
        </span>
      )}
    </div>
  );
}
